import React, { useState, useEffect, useRef } from 'react';

const CommandCenter = ({ 
  isOpen, 
  onClose, 
  searchQuery,
  onSearchChange,
  searchResults,
  onResultClick,
  onClearSearch,
  clientAvatarUrls = {}
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Reset selection when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchResults]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      const totalResults = searchResults.length;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % totalResults);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + totalResults) % totalResults);
          break;
        case 'Enter':
          e.preventDefault();
          if (searchResults[selectedIndex]) {
            onResultClick(searchResults[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, searchResults, onResultClick, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current && isOpen) {
      const selectedElement = resultsRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex, isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[150] backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Command Center */}
      <div className="fixed inset-0 z-[150] flex items-start justify-center pt-[20vh] pointer-events-none">
        <div 
          className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl mx-4 pointer-events-auto overflow-hidden"
          style={{ maxHeight: '60vh' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search Input */}
          <div className="relative border-b border-gray-200 dark:border-gray-700">
            <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search clients & properties..."
              className="w-full pl-12 pr-20 py-4 text-lg bg-transparent border-none focus:outline-none focus:ring-0 text-gray-900 dark:text-white placeholder-gray-400"
            />
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
              {searchQuery && (
                <button
                  onClick={() => {
                    onClearSearch();
                    if (inputRef.current) inputRef.current.focus();
                  }}
                  className="text-gray-400 hover:text-gray-600 text-sm"
                >
                  Clear
                </button>
              )}
              <kbd className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100 dark:bg-gray-700 rounded">
                ESC
              </kbd>
            </div>
          </div>

          {/* Results */}
          <div 
            ref={resultsRef}
            className="overflow-y-auto"
            style={{ maxHeight: 'calc(60vh - 65px)' }}
          >
            {searchResults.length === 0 && searchQuery ? (
              <div className="p-8 text-center text-gray-500">
                <div className="text-4xl mb-2">🔍</div>
                <p className="text-sm">No results found for "{searchQuery}"</p>
              </div>
            ) : searchResults.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <div className="text-4xl mb-2">⌘K</div>
                <p className="text-sm mb-2">Search for clients and properties</p>
                <div className="flex items-center justify-center gap-4 text-xs mt-4">
                  <span className="flex items-center gap-1">
                    <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">↑↓</kbd>
                    Navigate
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">↵</kbd>
                    Select
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">ESC</kbd>
                    Close
                  </span>
                </div>
              </div>
            ) : (
              <div className="py-2">
                {(searchResults || []).map((result, index) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => onResultClick(result)}
                    className={`w-full px-4 py-3 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      index === selectedIndex ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500' : ''
                    } ${result.isSubResult ? 'pl-12 py-2' : ''}`}
                  >
                    {/* Navigation Icon, Client Avatar or Property Thumbnail */}
                    {result.type === 'navigation' ? (
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center text-2xl">
                          {result.icon}
                        </div>
                      </div>
                    ) : result.type === 'client' ? (
                      <div className="flex-shrink-0 relative">
                        {result.avatar_url && clientAvatarUrls[result.avatar_url] ? (
                          <img 
                            src={clientAvatarUrls[result.avatar_url]} 
                            alt={result.title}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : null}
                        <div 
                          className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold"
                          style={{ display: (result.avatar_url && clientAvatarUrls[result.avatar_url]) ? 'none' : 'flex' }}
                        >
                          {(result.title || '').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                        </div>
                      </div>
                    ) : result.type === 'client-property' ? (
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center text-gray-500 text-sm">
                          🏠
                        </div>
                      </div>
                    ) : (
                      <div className="flex-shrink-0">
                        {result.thumbnail_url ? (
                          <img 
                            src={result.thumbnail_url} 
                            alt={result.title}
                            className="w-12 h-12 rounded object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded bg-gray-200 flex items-center justify-center text-gray-400 text-2xl">
                            🏠
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0 text-left">
                      <div className={`font-medium text-gray-900 dark:text-white truncate text-left ${result.isSubResult ? 'text-sm' : ''}`}>
                        {result.title}
                      </div>
                      {/* Show subtitle for navigation, properties, and clients */}
                      {result.type === 'navigation' && result.subtitle && (
                        <div className="text-sm text-gray-500 dark:text-gray-400 truncate mt-1 text-left">
                          {result.subtitle}
                        </div>
                      )}
                      {result.type === 'property' && (result.subtitle || result.date) && (
                        <div className="text-sm text-gray-500 dark:text-gray-400 truncate mt-1 text-left">
                          {result.subtitle}{result.subtitle && result.date && ' • '}{result.date}
                        </div>
                      )}
                      {result.type === 'client-property' && result.subtitle && (
                        <div className="text-xs text-gray-400 dark:text-gray-500 truncate text-left">
                          {result.subtitle}
                        </div>
                      )}
                      {result.type === 'client' && (
                        <div className="text-left">
                          {result.company && (
                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                              {result.company}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        result.type === 'navigation' ? 'bg-purple-100 text-purple-700' : 
                        result.type === 'client' ? 'bg-blue-100 text-blue-700' : 
                        result.type === 'client-property' ? 'bg-gray-100 text-gray-600' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {result.type === 'navigation' ? 'Page' : result.type === 'client' ? 'Client' : result.type === 'client-property' ? 'Property' : 'Property'}
                      </span>
                      {index === selectedIndex && (
                        <kbd className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100 dark:bg-gray-700 rounded">
                          ↵
                        </kbd>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer with hints */}
          {searchResults.length > 0 && (
            <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-xs text-gray-500">
              <span>{searchResults.length} result{searchResults.length !== 1 ? 's' : ''}</span>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <kbd className="px-2 py-1 bg-white dark:bg-gray-800 rounded">↑↓</kbd>
                  Navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-2 py-1 bg-white dark:bg-gray-800 rounded">↵</kbd>
                  Open
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CommandCenter;
